import { useCallback, useEffect } from "react"

import { useNavigation, useRouter } from "expo-router"
import { usePostHog } from "posthog-react-native"

import { SelectSponsor } from "src/components/SelectSponsor"
import { useAuth } from "src/context/AuthContext"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { SponsorInfo } from "src/services/api/api.types"

export default function SelectSponsorScreen() {
  const router = useRouter()
  const navigation = useNavigation()
  const { setMode } = useAvatarMode()
  const { pendingChannelId, clearPendingChannelId } = useChannelLink()
  const posthog = usePostHog()
  const { isAuthenticated } = useAuth()

  // If auth tokens are cleared (e.g. sponsor-list 401 during migration from
  // localStorage→sessionStorage sponsorId, or any other session expiry),
  // navigate back to login. Without this guard the sponsorListQuery becomes
  // disabled before it can error, isLoadingSponsors stays true, and the screen
  // shows "Loading…" indefinitely with no exit path.
  useEffect(() => {
    if (!isAuthenticated) {
      router.replace("/unauth/login")
    }
  }, [isAuthenticated, router])

  useEffect(() => {
    navigation.setOptions({ headerShown: !pendingChannelId })
  }, [navigation, pendingChannelId])

  const handleSwitchComplete = useCallback(() => {
    const channelFromLink = pendingChannelId
    clearPendingChannelId()
    if (channelFromLink) {
      posthog?.capture("channel_link_entry_success", { channelId: channelFromLink })
    }
    setMode("large", false)
    router.replace("/")
  }, [router, setMode, clearPendingChannelId, posthog, pendingChannelId])

  const handleTargetChannelNotFound = useCallback(
    (_sponsors: SponsorInfo[]) => {
      clearPendingChannelId()
      router.replace("/setup/channel-access-denied")
    },
    [clearPendingChannelId, router],
  )

  return (
    <SelectSponsor
      onSwitchComplete={handleSwitchComplete}
      targetChannelId={pendingChannelId ?? undefined}
      onTargetChannelNotFound={handleTargetChannelNotFound}
    />
  )
}
